/**
 * iter-110.6 — Schema Match Preview card
 *
 * Renders for an uploaded dataset:
 *   • Active model match (with confidence bar + auto/manual badge)
 *   • Per-column mapping (canonical_field → csv_column with check/edit)
 *   • Alternate candidate models (for one-click reassignment)
 *   • Unmapped columns banner
 *   • Manual remap row by row (Select-driven)
 *   • Save → triggers backend writeback so the dashboard refreshes
 *
 * No hardcoded model names — everything is sourced from
 * `GET /api/data/datasets/{id}/schema-match`.
 */
import React, { useEffect, useMemo, useState } from "react";import api from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth-context";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CheckCircle2, AlertCircle, Loader2, Wand2, Save, RefreshCw, X, Sparkles, Plus } from "lucide-react";
import SchemaMappingOnboardingTour from "@/components/data/SchemaMappingOnboardingTour";
// iter-120 round-4 follow-up #3 — NewModelDialog removed (replaced by
// the auto-create dropdown option). No manual field-by-field UI.

export default function SchemaMatchPreview({ datasetId, onClose, onSaved }) {
    const { user } = useAuth();
    const isSuperAdmin = user?.role === "super_admin";
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [editMode, setEditMode] = useState(false);
    // Working copy of the field map while user remaps
    const [draftModelId, setDraftModelId] = useState(null);
    const [draftMap, setDraftMap] = useState({});
    // iter-110.7: AI-suggested mapping result
    const [aiSuggesting, setAiSuggesting] = useState(false);
    const [aiResult, setAiResult] = useState(null);
    // iter-120 (Feb 2026) — SA-only "Create new model" inline dialog.
    // iter-120 round-4 follow-up #3 — auto-create model flow state.
    const [autoCreating, setAutoCreating] = useState(false);

    const handleAutoCreateModel = async () => {
        if (autoCreating) return;
        setAutoCreating(true);
        try {
            const r = await api.post(`/data/datasets/${datasetId}/auto-create-model`);
            const newModel = r.data?.model;
            if (newModel?.id) {
                toast.success(`Created canonical model "${newModel.name}" with ${newModel.fields?.length || 0} fields`);
                await reload();
                // Auto-select the new model and skip straight into edit mode
                // so user can refine if needed.
                setEditMode(false);
                setDraftModelId(newModel.id);
            } else {
                toast.error("Model created but no payload returned");
            }
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Auto-create failed");
        } finally {
            setAutoCreating(false);
        }
    };

    const reload = async () => {
        setLoading(true);
        try {
            const r = await api.get(`/data/datasets/${datasetId}/schema-match`);
            setData(r.data);
            setDraftModelId(r.data?.active_model?.model_id || null);
            setDraftMap(r.data?.field_map || {});
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Could not load schema match");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (datasetId) reload();
        // eslint-disable-next-line
    }, [datasetId]);

    // Resolve candidate fields for the currently-drafted model so the user
    // can map each canonical field to one of their CSV columns. iter-120
    // (Feb 2026): we now also look inside `data.all_models` so a model
    // chosen from the empty-state "Pick any canonical model" dropdown
    // also resolves to a draft candidate with field metadata.
    const draftCandidate = useMemo(() => {
        if (!data || !draftModelId) return null;
        const fromCandidates = (data.candidates || []).find((c) => c.model_id === draftModelId);
        if (fromCandidates) return fromCandidates;
        const fromAll = (data.all_models || []).find((m) => m.model_id === draftModelId);
        if (fromAll) {
            // Synthesise a candidate-shaped object so the rest of the
            // component (allCanonicalFields, fieldMeta, save) works as-is.
            return {
                model_id: fromAll.model_id,
                model_slug: fromAll.model_slug,
                model_name: fromAll.model_name,
                confidence: 0,
                field_map: {},
                matched_fields: [],
                missing_required: [],
                all_fields: fromAll.all_fields || [],
            };
        }
        return null;
    }, [data, draftModelId]);

    const allCanonicalFields = useMemo(() => {
        if (!draftCandidate) return [];
        // iter-110.7: prefer the full canonical field list when the backend
        // provides it (so the remap UI can show every possible mapping slot,
        // not just the matched/required ones). Falls back to the old union
        // behavior for older backend payloads.
        if (Array.isArray(draftCandidate.all_fields) && draftCandidate.all_fields.length) {
            const names = draftCandidate.all_fields.map((f) => f.name);
            // Append any manually-drafted fields that aren't in the canonical
            // list (shouldn't happen with validation, but belt-and-suspenders).
            Object.keys(draftMap).forEach((k) => {
                if (!names.includes(k)) names.push(k);
            });
            return names;
        }
        const set = new Set([
            ...draftCandidate.matched_fields.map((f) => f.field_name),
            ...draftCandidate.missing_required,
            ...Object.keys(draftMap),
        ]);
        return Array.from(set);
    }, [draftCandidate, draftMap]);

    // iter-110.7: expose metadata (type / required / description) so the UI
    // can show helpful hints next to each canonical field name.
    const fieldMeta = useMemo(() => {
        const m = {};
        (draftCandidate?.all_fields || []).forEach((f) => { m[f.name] = f; });
        return m;
    }, [draftCandidate]);

    const csvColumns = useMemo(() => (data?.columns || []).map((c) => c.name), [data]);

    const setMapping = (canonField, csvCol) => {
        setDraftMap((m) => {
            const next = { ...m };
            if (!csvCol) delete next[canonField];
            else next[canonField] = csvCol;
            return next;
        });
    };

    const switchToCandidate = (modelId) => {
        // iter-120 (Feb 2026): also accept models from `data.all_models`
        // so the empty-state picker works.
        const c = (data?.candidates || []).find((x) => x.model_id === modelId);
        const fromAll = !c && (data?.all_models || []).find((m) => m.model_id === modelId);
        if (!c && !fromAll) return;
        setDraftModelId(modelId);
        const seed = c?.field_map || {};
        setDraftMap(seed);
        setEditMode(true);
        // iter-120 (Feb 2026) — auto-trigger Sense AI on every model
        // pick. Suggestions are merged DIRECTLY into the draft map so
        // the per-field dropdowns are pre-populated; the user can then
        // tweak via the dropdown without ever having to click an AI
        // button. The legacy "Ask Sense AI" button is gone — auto-pull
        // is one less click and matches the "smart by default" UX.
        // We pass the seed (heuristic map for `candidates` picks, {}
        // for `all_models` picks) so the LLM only fills the gaps.
        askAi({ modelId, seedMap: seed, autoApply: true }).catch(() => { /* toast handled inside */ });
    };

    const save = async () => {
        if (!draftModelId) {
            toast.error("Pick a canonical model first");
            return;
        }
        if (!Object.keys(draftMap).length) {
            toast.error("Map at least one field before saving");
            return;
        }
        setSaving(true);
        try {
            const r = await api.patch(`/data/datasets/${datasetId}/field-map`, {
                model_id: draftModelId,
                field_map: draftMap,
            });
            const wb = r.data?.writeback || {};
            toast.success("Mapping saved", {
                description: `${wb.inserted || 0} inserted · ${wb.updated || 0} updated · ${wb.total || 0} rows written to ${draftCandidate?.model_name || "model"}`,
            });
            await reload();
            setEditMode(false);
            if (onSaved) onSaved(r.data);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Could not save mapping");
        } finally {
            setSaving(false);
        }
    };

    const clearOverride = async () => {
        setSaving(true);
        try {
            await api.patch(`/data/datasets/${datasetId}/field-map`, { model_id: null });
            toast.success("Manual remap cleared — re-running auto-match");
            await reload();
            setEditMode(false);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Could not clear override");
        } finally {
            setSaving(false);
        }
    };

    // iter-110.7 / iter-120 — Sense AI assist for any unmapped CSV columns.
    // Now invoked AUTOMATICALLY on every model pick (see `switchToCandidate`)
    // so the per-field dropdowns are pre-populated with the LLM's best
    // guess. The user can override via the dropdown — no buttons, no
    // banner-to-apply flow. The `opts.autoApply` flag merges suggestions
    // straight into `draftMap`; `opts.modelId` / `opts.seedMap` let the
    // caller drive without first having to flush state via setState.
    //
    // Safe to call repeatedly: if the LLM returns nothing useful we
    // silently surface a non-blocking info toast and leave the draft
    // alone.
    const askAi = async ({ modelId, seedMap, autoApply = false } = {}) => {
        const targetModel = modelId || draftModelId;
        if (!targetModel) {
            toast.error("Pick a canonical model first");
            return;
        }
        setAiSuggesting(true);
        setAiResult(null);
        try {
            const r = await api.post(`/data/datasets/${datasetId}/suggest-mapping`, {
                model_id: targetModel,
                // iter-120 (Feb 2026) — send the live draft (or the
                // explicit seed passed by switchToCandidate) so the
                // backend doesn't re-seed via the heuristic matcher.
                field_map: (seedMap !== undefined ? seedMap : draftMap) || {},
            });
            setAiResult(r.data);
            const llmUnavailable = !!r.data?.llm_unavailable;
            const suggestions = r.data?.suggestions || {};
            const sCount = Object.keys(suggestions).length;
            if (llmUnavailable) {
                // No LLM provider configured for this tenant. Stay silent
                // in the auto-trigger path — the dedicated inline banner
                // (rendered below) tells the user how to enable it.
                if (!autoApply) {
                    toast.info("Sense AI is unavailable for this tenant", {
                        description: r.data?.reasoning || "Ask an admin to enable an LLM provider in Settings → LLM Providers.",
                    });
                }
                return;
            }
            if (sCount === 0) {
                // Auto-trigger path: stay silent (no toast spam) — the
                // user sees the empty dropdowns and can map manually.
                if (!autoApply) {
                    toast.info("Sense AI couldn't find any additional mappings", {
                        description: r.data?.reasoning || "Every canonical field already mapped.",
                    });
                }
                return;
            }
            if (autoApply) {
                // Merge directly into the draft so dropdowns populate.
                setDraftMap((m) => ({ ...(m || {}), ...suggestions }));
                toast.success(`Sense AI auto-mapped ${sCount} field${sCount === 1 ? "" : "s"}`, {
                    description: "Review the dropdowns and adjust any that don't fit, then Save.",
                });
            } else {
                toast.success(`Sense AI suggested ${sCount} mapping${sCount === 1 ? "" : "s"}`, {
                    description: "Review below and click Apply to merge into your draft.",
                });
            }
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Could not get AI suggestions");
        } finally {
            setAiSuggesting(false);
        }
    };

    // iter-120 (Feb 2026) — the legacy `applyAiSuggestions` helper was
    // removed; AI suggestions now apply directly inside `askAi` when
    // invoked from `switchToCandidate` with `autoApply: true`.

    if (loading || !data) {
        return (
            <div className="border border-border rounded-sm bg-card p-6 flex items-center gap-2 text-sm text-muted-foreground" data-testid="schema-match-loading">
                <Loader2 className="h-4 w-4 animate-spin" /> Analysing column match…
            </div>
        );
    }

    if (!data.active_model && !(editMode && draftModelId)) {
        // iter-120 (Feb 2026) — empty-state fix. When neither auto-match
        // nor the closest-candidate heuristic surfaced any model, fall
        // back to the FULL canonical-model list (`data.all_models`) so
        // the user can ALWAYS manually pick one and start mapping.
        const closest = data.candidates || [];
        const otherModels = data.all_models || [];
        const hasAny = closest.length > 0 || otherModels.length > 0;
        return (
            <div className="space-y-4">
                {/* iter-120 (Feb 2026) — one-time onboarding tour. Renders
                    once per user; the dismiss flag is persisted to
                    `user.ui_prefs.seen_schema_mapping_onboarding`. */}
                <SchemaMappingOnboardingTour />
                <div className="border border-amber-500/40 bg-amber-500/5 rounded-sm p-4" data-testid="schema-match-no-model">
                    <div className="flex items-start gap-3">
                    <AlertCircle className="h-4 w-4 text-amber-600 mt-0.5 shrink-0" />
                    <div className="flex-1 min-w-0">
                        <div className="text-sm font-semibold">No canonical model auto-matched</div>
                        <div className="text-xs text-muted-foreground mt-1">
                            None of the canonical data models matched your columns with enough confidence.
                            Pick a model below — we'll then show every canonical field so you can map
                            each one to one of your columns.
                        </div>
                        {!hasAny && (
                            <div className="text-xs text-muted-foreground mt-3 italic">
                                No canonical models exist yet. Ask your Super Admin to publish a model
                                via Settings → Data Models.
                            </div>
                        )}
                        {closest.length > 0 && (
                            <div className="mt-3">
                                <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1.5">Closest candidates</div>
                                <div className="flex flex-wrap gap-1.5">
                                    {closest.slice(0, 5).map((c) => (
                                        <Button key={c.model_id} variant="outline" size="sm"
                                                className="h-7 rounded-sm font-mono text-[11px]"
                                                onClick={() => switchToCandidate(c.model_id)}
                                                data-testid={`schema-match-fallback-${c.model_slug}`}>
                                            {c.model_name} <span className="opacity-60 ml-1.5">{(c.confidence * 100).toFixed(0)}%</span>
                                        </Button>
                                    ))}
                                </div>
                            </div>
                        )}
                        {(otherModels.length > 0 || isSuperAdmin) && (
                            <div className="mt-4" data-testid="schema-match-all-models-picker">
                                <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1.5">
                                    {isSuperAdmin
                                        ? "Pick an existing model OR create a new one"
                                        : "Or pick any canonical model"}
                                </div>
                                <Select onValueChange={(v) => {
                                    if (v === "__auto_create__") {
                                        handleAutoCreateModel();
                                    } else {
                                        switchToCandidate(v);
                                    }
                                }}>
                                    <SelectTrigger className="h-8 rounded-sm text-xs max-w-md" data-testid="schema-match-all-models-trigger">
                                        <SelectValue placeholder={isSuperAdmin ? "Select or create…" : "Select a canonical model…"} />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {/* iter-120 round-4 follow-up #3 — SA-only "Create New Model"
                                            option at the top of the dropdown. Builds a canonical model
                                            from the dataset's columns automatically — NO manual
                                            field-by-field entry. */}
                                        {isSuperAdmin && (
                                            <SelectItem
                                                value="__auto_create__"
                                                data-testid="schema-match-create-new-model-option"
                                                className="font-medium text-primary"
                                            >
                                                <Plus className="h-3 w-3 inline mr-1.5" />
                                                Create New Model (auto from columns)
                                            </SelectItem>
                                        )}
                                        {otherModels.map((m) => (
                                            <SelectItem key={m.model_id} value={m.model_id}
                                                        data-testid={`schema-match-all-models-option-${m.model_slug}`}>
                                                {m.model_name}
                                                <span className="opacity-60 ml-2 font-mono text-[10px]">model.{m.model_slug}</span>
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                                <div className="text-[10px] text-muted-foreground mt-1.5">
                                    {isSuperAdmin
                                        ? <>Pick an existing model to map columns manually, or choose <span className="font-mono">Create New Model</span> to auto-generate one from your dataset.</>
                                        : <>After picking a model, every canonical field appears as a row — drop your column into each slot, then click <span className="font-mono">Save mapping</span> to attach.</>}
                                </div>
                                {autoCreating && (
                                    <div className="mt-2 text-[11px] text-muted-foreground flex items-center gap-1.5" data-testid="schema-match-auto-creating">
                                        <Loader2 className="h-3 w-3 animate-spin" />
                                        Building canonical model from your columns…
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            </div>
            </div>
        );
    }

    // iter-120 (Feb 2026): when the user picked a model from the "Pick any
    // canonical model" empty-state dropdown, `data.active_model` is still
    // null on this render. Synthesise a manual-mode active model from the
    // draft so the header / confidence bar / field rows below render
    // correctly without null-derefs.
    const am = data.active_model || (draftCandidate ? {
        model_id: draftCandidate.model_id,
        model_slug: draftCandidate.model_slug,
        model_name: draftCandidate.model_name,
        confidence: 0,
        source: "manual",
    } : null);
    if (!am) return null; // belt-and-suspenders — unreachable in normal flow
    const confidencePct = Math.round((am.confidence || 0) * 100);
    const confColor = confidencePct >= 90 ? "bg-[hsl(var(--success))]" : confidencePct >= 70 ? "bg-amber-500" : "bg-orange-500";

    // iter-120 round-3 follow-up #5 · When the dataset was already
    // loaded into the canonical model via a connector writeback
    // (target_model_slug stamp), show a "Mapped" success card instead
    // of the heavy per-column editor. The schema fields ARE the
    // canonical fields — there's no manual mapping to do. User can
    // still click "Remap" to override, which falls back to the
    // standard editor below.
    if (am.source === "writeback" && !editMode) {
        return (
            <div className="space-y-4" data-testid="schema-match-writeback">
                <div className="border border-[hsl(var(--success))]/30 bg-[hsl(var(--success))]/5 rounded-sm p-4 flex items-start gap-3">
                    <CheckCircle2 className="h-5 w-5 text-[hsl(var(--success))] mt-0.5 shrink-0" />
                    <div className="flex-1 min-w-0">
                        <div className="text-sm font-semibold mb-1">
                            Loaded into canonical model
                        </div>
                        <div className="text-xs text-muted-foreground">
                            This dataset is already mapped to{" "}
                            <span className="font-mono text-foreground">model.{am.model_slug}</span>
                            {am.model_name && am.model_name !== am.model_slug && ` (${am.model_name})`}.
                            The connector's writeback uses the canonical schema directly — no
                            column-level mapping required. {data.columns.length} columns are
                            available for dashboards and queries.
                        </div>
                        <div className="mt-3 flex items-center gap-2">
                            <Badge variant="outline" className="font-mono text-[10px] gap-1">
                                <CheckCircle2 className="h-2.5 w-2.5 text-[hsl(var(--success))]" />
                                model.{am.model_slug}
                            </Badge>
                            <Badge variant="outline" className="font-mono text-[10px]">
                                {data.columns.length} columns
                            </Badge>
                        </div>
                    </div>
                    <Button variant="outline" size="sm" className="h-7 rounded-sm shrink-0"
                            onClick={() => setEditMode(true)}
                            data-testid="schema-match-writeback-remap">
                        Remap manually
                    </Button>
                </div>
            </div>
        );
    }

    return (
        <div className="space-y-4">
            {/* iter-120 (Feb 2026) — one-time onboarding tour above the
                main editor too, so users whose CSV DID auto-match still
                see it once. The component self-hides after dismiss. */}
            <SchemaMappingOnboardingTour />
            <div className="border border-border rounded-sm bg-card" data-testid="schema-match-preview">
                <div className="px-5 py-4 border-b border-border flex items-start gap-3">
                    <Wand2 className="h-4 w-4 text-primary mt-0.5 shrink-0" />
                <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-sm font-semibold">Schema match</span>
                        <Badge variant="outline" className="font-mono text-[10px] gap-1">
                            <CheckCircle2 className="h-2.5 w-2.5 text-[hsl(var(--success))]" />
                            model.{am.model_slug}
                        </Badge>
                        <Badge variant={am.source === "manual" ? "default" : "outline"} className="font-mono text-[10px]" data-testid="schema-match-source-badge">
                            {am.source === "manual" ? "manual remap" : "auto-matched"}
                        </Badge>
                    </div>
                    <div className="text-xs text-muted-foreground mt-1 font-mono">
                        Mapping <span className="text-foreground">{Object.keys(data.field_map).length}</span> of <span className="text-foreground">{data.columns.length}</span> columns to {am.model_name}
                        {data.unmapped_columns.length > 0 && (
                            <> · <span className="text-amber-600">{data.unmapped_columns.length} unmapped</span></>
                        )}
                    </div>
                    {/* Confidence bar */}
                    <div className="mt-2 flex items-center gap-2">
                        <div className="h-1.5 flex-1 bg-muted rounded-full overflow-hidden max-w-[240px]">
                            <div className={`h-full ${confColor} transition-all`} style={{ width: `${confidencePct}%` }} />
                        </div>
                        <span className="text-[10px] font-mono text-muted-foreground">{confidencePct}% confidence</span>
                    </div>
                </div>
                <div className="flex items-center gap-1.5">
                    {!editMode && (
                        <Button variant="outline" size="sm" className="h-7 rounded-sm" onClick={() => setEditMode(true)} data-testid="schema-match-edit">
                            Remap
                        </Button>
                    )}
                    {editMode && aiSuggesting && (
                        <div className="flex items-center gap-1.5 text-[11px] font-mono text-primary px-2"
                             data-testid="schema-match-ai-loading">
                            <Loader2 className="h-3 w-3 animate-spin" />
                            Sense AI auto-mapping…
                        </div>
                    )}
                    {am.source === "manual" && !editMode && (
                        <Button variant="ghost" size="sm" className="h-7 rounded-sm" onClick={clearOverride} disabled={saving} data-testid="schema-match-reset">
                            <RefreshCw className="h-3 w-3 mr-1" /> Reset to auto
                        </Button>
                    )}
                    {onClose && (
                        <Button variant="ghost" size="sm" className="h-7 w-7 p-0 rounded-sm" onClick={onClose}>
                            <X className="h-3.5 w-3.5" />
                        </Button>
                    )}
                </div>
            </div>

            {/* Per-column mapping */}
            <div className="p-5 space-y-2">
                {/* iter-120 (Feb 2026) — soft banner when the tenant has
                    no LLM provider configured. Renders only after the
                    auto-trigger has returned `llm_unavailable: true` so
                    we don't bother orgs that just haven't picked a model
                    yet. Includes a deep-link to Settings → LLM Providers
                    so the admin can fix it in one click. */}
                {editMode && aiResult?.llm_unavailable && (
                    <div className="border border-amber-500/40 bg-amber-500/5 rounded-sm p-3 mb-3 flex items-start gap-2"
                         data-testid="schema-match-ai-unavailable">
                        <AlertCircle className="h-3.5 w-3.5 text-amber-600 mt-0.5 shrink-0" />
                        <div className="flex-1 min-w-0">
                            <div className="text-xs font-semibold mb-1">Sense AI auto-mapping is unavailable</div>
                            <div className="text-[11px] text-muted-foreground">
                                {aiResult.reasoning || "No LLM provider is enabled for this tenant."}
                                {" "}
                                Ask an admin to enable one under{" "}
                                <a href="/settings/llm-providers" className="text-primary hover:underline">
                                    Settings → LLM Providers
                                </a>
                                . You can still map columns manually below.
                            </div>
                        </div>
                        <Button size="sm" variant="ghost" className="h-7 rounded-sm shrink-0"
                                onClick={() => setAiResult(null)}
                                data-testid="schema-match-ai-unavailable-dismiss">
                            <X className="h-3 w-3" />
                        </Button>
                    </div>
                )}
                {/* iter-120 (Feb 2026): read-only summary of what Sense AI
                    auto-mapped after the user picked a model. Suggestions
                    are ALREADY merged into the draft (so dropdowns are
                    pre-populated); this card just explains the reasoning
                    so the user can sanity-check before saving. */}
                {editMode && aiResult && Object.keys(aiResult.suggestions || {}).length > 0 && (
                    <div className="border border-primary/40 bg-primary/5 rounded-sm p-3 mb-3" data-testid="schema-match-ai-banner">
                        <div className="flex items-start gap-2">
                            <Sparkles className="h-3.5 w-3.5 text-primary mt-0.5 shrink-0" />
                            <div className="flex-1 min-w-0">
                                <div className="text-xs font-semibold mb-1">
                                    Sense AI auto-mapped {Object.keys(aiResult.suggestions).length} field{Object.keys(aiResult.suggestions).length === 1 ? "" : "s"}
                                </div>
                                <div className="space-y-0.5">
                                    {Object.entries(aiResult.suggestions).map(([canon, csv]) => (
                                        <div key={canon} className="text-[11px] font-mono">
                                            <span className="text-foreground">{canon}</span>
                                            <span className="text-muted-foreground"> ← </span>
                                            <span className="text-primary">{csv}</span>
                                        </div>
                                    ))}
                                </div>
                                {aiResult.reasoning && (
                                    <div className="text-[10px] text-muted-foreground italic mt-2">{aiResult.reasoning}</div>
                                )}
                                <div className="text-[10px] text-muted-foreground mt-2">
                                    Review the dropdowns below — adjust any that don't fit, then click Save.
                                </div>
                            </div>
                            <Button size="sm" variant="ghost" className="h-7 rounded-sm shrink-0" onClick={() => setAiResult(null)} data-testid="schema-match-ai-dismiss">
                                <X className="h-3 w-3" />
                            </Button>
                        </div>
                    </div>
                )}

                {/* Alternate candidates strip */}
                {data.candidates.length > 1 && (
                    <div className="mb-3">
                        <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1.5">Other candidate models</div>
                        <div className="flex flex-wrap gap-1.5">
                            {data.candidates.filter((c) => c.model_id !== draftModelId).slice(0, 4).map((c) => (
                                <Button key={c.model_id} variant="outline" size="sm"
                                        className="h-7 rounded-sm font-mono text-[10px]"
                                        onClick={() => switchToCandidate(c.model_id)}
                                        data-testid={`schema-match-candidate-${c.model_slug}`}>
                                    {c.model_name}
                                    <span className="opacity-60 ml-1.5">{(c.confidence * 100).toFixed(0)}%</span>
                                </Button>
                            ))}
                        </div>
                    </div>
                )}

                {/* Field-by-field rows */}
                <div className="border border-border rounded-sm overflow-hidden">
                    <div className="grid grid-cols-[1fr_auto_1fr] gap-0 text-[10px] uppercase tracking-wider bg-muted/30 px-3 py-2 border-b border-border">
                        <span className="text-muted-foreground">Canonical field ({am.model_name})</span>
                        <span></span>
                        <span className="text-muted-foreground">Your column</span>
                    </div>
                    {allCanonicalFields.map((canon) => {
                        const csvCol = (editMode ? draftMap : data.field_map)[canon];
                        const matched = !!csvCol;
                        const meta = fieldMeta[canon] || {};
                        return (
                            <div key={canon} className="grid grid-cols-[1fr_auto_1fr] gap-2 items-center px-3 py-2 border-b border-border last:border-b-0 text-xs"
                                 data-testid={`schema-match-row-${canon}`}>
                                <div className="min-w-0">
                                    <div className="flex items-center gap-1.5">
                                        <span className="font-mono">{canon}</span>
                                        {meta.required && (
                                            <Badge variant="outline" className="text-[9px] px-1 py-0 h-4 border-amber-500/40 text-amber-700 dark:text-amber-300">required</Badge>
                                        )}
                                        {meta.type && (
                                            <span className="text-[10px] font-mono text-muted-foreground">· {meta.type}</span>
                                        )}
                                    </div>
                                    {meta.description && (
                                        <div className="text-[10px] text-muted-foreground italic mt-0.5 truncate">{meta.description}</div>
                                    )}
                                </div>
                                <span className={`text-xs ${matched ? "text-[hsl(var(--success))]" : "text-muted-foreground"}`}>
                                    {matched ? "→" : "·"}
                                </span>
                                {editMode ? (
                                    <Select value={csvCol || "__none__"} onValueChange={(v) => setMapping(canon, v === "__none__" ? null : v)}>
                                        <SelectTrigger className="h-7 rounded-sm text-[11px]" data-testid={`schema-match-select-${canon}`}>
                                            <SelectValue placeholder="— unmapped —" />
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="__none__">— unmapped —</SelectItem>
                                            {csvColumns.map((col) => (
                                                <SelectItem key={col} value={col} data-testid={`schema-match-option-${canon}-${col}`}>{col}</SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                ) : (
                                    <span className={`font-mono text-[11px] ${matched ? "text-foreground" : "text-muted-foreground italic"}`}>
                                        {csvCol || "— unmapped —"}
                                    </span>
                                )}
                            </div>
                        );
                    })}
                </div>

                {/* Unmapped columns */}
                {data.unmapped_columns.length > 0 && !editMode && (
                    <div className="text-[11px] font-mono text-muted-foreground" data-testid="schema-match-unmapped">
                        Unused columns: {data.unmapped_columns.map((c) => (
                            <span key={c} className="inline-block mr-1.5 opacity-70">{c}</span>
                        ))}
                    </div>
                )}

                {editMode && (
                    <div className="flex items-center gap-2 pt-2 border-t border-border mt-3">
                        <Button onClick={save} disabled={saving} className="rounded-sm" data-testid="schema-match-save">
                            {saving ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <Save className="h-3.5 w-3.5 mr-1.5" />}
                            Save mapping & re-ingest
                        </Button>
                        <Button variant="ghost" onClick={() => { setEditMode(false); setDraftModelId(am.model_id); setDraftMap(data.field_map); }}
                                disabled={saving} className="rounded-sm" data-testid="schema-match-cancel">
                            Cancel
                        </Button>
                        <span className="text-[10px] font-mono text-muted-foreground ml-auto">
                            Saving will rebuild dashboard data from this dataset.
                        </span>
                    </div>
                )}
            </div>
            </div>
            {/* iter-120 round-4 follow-up #3 — NewModelDialog (manual
                field-by-field editor) removed per user direction.
                Auto-create now flows entirely through the dropdown's
                "Create New Model" option, which calls
                POST /data/datasets/{id}/auto-create-model and lets the
                backend infer the schema. */}
        </div>
    );
}
